import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, Package, Sparkles } from 'lucide-react';

import { API_STRING as api } from '@/App';
import ProductCard from '@/components/ProductCard';
import Footer from '@/components/Footer';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

// Public "Shop this collection" page for a Featured Section. Shareable on
// socials; product links carry the baked-in affiliate templates.
export default function SharedCollection({ user }) {
  const { sectionId } = useParams();
  const darkMode = useDarkMode();
  const [collection, setCollection] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing

  useEffect(() => {
    fetch(`${api}/featured-sections/${sectionId}/public`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setCollection(d); setStatus('ok'); })
      .catch(() => setStatus('missing'));
  }, [sectionId]);

  useDocumentMeta({
    title: collection ? `${collection.name}, a Mementa collection` : 'Gift Collection, Mementa',
    description: collection?.description || 'A hand-picked gift collection from Mementa.',
    url: `https://mementa.gifts/collection/${sectionId}`,
  });

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: darkMode.bgPrimary }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-4" style={{ borderColor: '#FED7A5' }} />
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center" style={{ background: darkMode.bgPrimary }} data-testid="collection-not-found">
        <Gift className="w-14 h-14 mb-4" style={{ color: '#FED7A5' }} />
        <h1 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: 600 }}>
          This collection isn&rsquo;t available
        </h1>
        <p className="text-sm mb-6" style={{ color: darkMode.textSecondary }}>
          It may have been unpublished or the link is out of date.
        </p>
        <Link
          to="/browse"
          className="px-6 py-2.5 rounded-full font-semibold text-sm"
          style={{ background: '#9E6752', color: 'white' }}
        >
          Explore Mementa
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors page-enter" style={{ background: darkMode.bgPrimary }} data-testid="shared-collection-page">
      {/* Collection hero */}
      <div className="px-4 pt-14 pb-10 text-center" style={{ background: darkMode.isDark ? 'linear-gradient(135deg, #2D4354 0%, #534145 100%)' : '#FFF8F0', borderBottom: '2px solid #FED7A5' }}>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-3 px-3 py-1 rounded-full" style={{ background: '#FED7A5', color: '#20212B' }}>
          <Sparkles className="w-3.5 h-3.5" />
          A hand-picked collection from Mementa
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.isDark ? '#FED7A5' : '#20212B', fontWeight: 700 }} data-testid="collection-title">
          {collection.name}
        </h1>
        {collection.description && (
          <p className="text-base max-w-2xl mx-auto" style={{ color: darkMode.isDark ? '#E5E5E5' : '#534145' }}>
            {collection.description}
          </p>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {collection.items.map((item) => {
          if (item.type === 'deck') {
            return (
              <div key={`deck-${item.deck.id}`} className="mb-10 p-4 sm:p-6 rounded-2xl" style={{ border: '2px dashed #FED7A5' }} data-testid={`collection-deck-${item.deck.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-4 h-4" style={{ color: '#9E6752' }} />
                  <h2 className="text-lg sm:text-xl" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: 600 }}>
                    {item.deck.title}
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#FED7A5', color: '#20212B' }}>Bundle</span>
                </div>
                {item.deck.description && (
                  <p className="text-sm mb-4" style={{ color: darkMode.textSecondary }}>{item.deck.description}</p>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {item.products.map((p) => (
                    <ProductCard key={p.id} product={p} user={user} source="shared_collection" />
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })}

        {collection.items.some((i) => i.type === 'product') && (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="collection-product-grid">
            {collection.items
              .filter((i) => i.type === 'product')
              .map((i) => (
                <ProductCard key={i.product.id} product={i.product} user={user} source="shared_collection" />
              ))}
          </div>
        )}

        {/* Funnel CTA */}
        <div className="mt-14 text-center rounded-2xl p-8" style={{ background: darkMode.isDark ? '#2D4354' : '#FFF8F0', border: '1px solid #FED7A5' }}>
          <h3 className="text-xl sm:text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: 600 }}>
            Love these picks?
          </h3>
          <p className="text-sm mb-5" style={{ color: darkMode.textSecondary }}>
            Mementa curates thoughtful gifts for every person and occasion.
          </p>
          <Link
            to="/browse"
            data-testid="collection-browse-cta"
            className="inline-block px-8 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
            style={{ background: '#9E6752', color: 'white' }}
          >
            Discover more gifts
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
